import React from 'react';
import { pageDefinition } from 'site-modules/shared/pages/page';
import { CmsModel } from 'client/data/models/cms';
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { updateVehicle } from 'client/actions/vehicle';
import { setCriticalCss } from 'client/actions/critical-css';
import {
  TypeRankingsModel,
  buildTypeRankingsPath,
  buildTypeRankingsPathForAboveTheFold,
  buildTypeRankingsPathForBelowTheFold,
} from 'client/data/models/type-rankings';
import { getTreehouseEligiblePath, TreehouseModel } from 'client/data/models/treehouse';
import { FeatureFlagModel } from 'client/data/models/feature-flag';
import { SuggestedVehicleModel } from 'client/data/models/suggested-vehicle';
import { getReviewLinksPath, getRankingsContentPath } from 'site-modules/shared/utils/upper-funnel/content-path';
import { getMostSoldTypeContentPath } from 'site-modules/shared/utils/upper-funnel/most-sold-type-infographic';
import { hasSubsegments } from 'site-modules/shared/utils/upper-funnel/type-rankings';
import { LinkWidgetsModel, getPageLinksPath } from 'client/data/models/link-widgets';
import { EditorialModel, buildTypeArticlesPath } from 'client/data/models/editorial';
import { TypePageDecorator } from 'site-modules/upper-funnel/decorators/type-page-decorator';
import { defineSeoContent } from 'site-modules/upper-funnel/utils/seo-content';
import { NativeAdsCreativeConfigModel } from 'client/data/models/native-ads-creative-config';
import { SEGMENT_LISTING_AD } from 'site-modules/shared/components/native-ad/utils/constants';
import {
  getCreativeConfigAdPathByVehicle,
  setAdsListToPreload,
  siteServedAdsCriticalCss,
} from 'site-modules/shared/components/native-ad/utils/utils';
import { TYPES_WITH_MOST_SOLD_GRAPH } from 'site-modules/shared/components/upper-funnel/most-sold-type-infographic/constants';
import { HERO_BG_PATHS_LAUREL } from 'site-modules/upper-funnel/components/type/hero-content/constants';
import { CAR_TYPES, PAGE_CAR_TYPES } from 'site-modules/shared/constants/upper-funnel/car-types';
import { PageModel } from 'client/data/models/page';
import { HomeModulesModel } from 'client/data/models/home-modules';
import { TypeLeaseDealsModel, TypeLeaseDealsPaths } from 'client/data/models/type-lease-deals';
import { buildLeaseDealsHiddenGemsCriticalCss } from 'site-modules/upper-funnel/utils/lease-deals-hidden-gems-critical-css';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';

import { TypePageV2 } from './type';

function preload(store, type, isBelowFoldPreload) {
  const preloader = new ModelPreloader(store);
  const { mobile } = store.getState();

  const rankingsPath = buildTypeRankingsPath(type);

  // this path resolves rankings path data first, then uses that to resolve above the fold data
  preloader.resolve(buildTypeRankingsPathForAboveTheFold(type), TypeRankingsModel);
  preloader.resolve(TypeLeaseDealsPaths.getTypeLeaseDealsTypeCategoryPath(type), TypeLeaseDealsModel);

  const vehicle = { type: { niceName: type } };
  store.dispatch(updateVehicle(vehicle));
  setAdsListToPreload(preloader, store.getState(), [SEGMENT_LISTING_AD.AD_NAME], vehicle);

  if (isBelowFoldPreload) {
    // resolves below the fold data by getting the rankings path data
    // rankings path data was already resolved in the previous preloader.load()
    preloader.resolve(buildTypeRankingsPathForBelowTheFold(type), TypeRankingsModel);

    if (type !== PAGE_CAR_TYPES.MINIVAN) {
      preloader.resolve('vehicleShowcaseContent', HomeModulesModel);
      preloader.resolve('homeHeroContent', HomeModulesModel);
    }

    if (type === CAR_TYPES.ELECTRIC) {
      preloader.resolve(getTreehouseEligiblePath(), TreehouseModel);
    }

    preloader.resolve(`content["${getReviewLinksPath(type)}"]`, CmsModel);
    preloader.resolve(`content["${getRankingsContentPath(type)}"]`, CmsModel);
    preloader.resolve(`content["${getMostSoldTypeContentPath(type)}"]`, CmsModel);
    preloader.resolve(buildTypeArticlesPath(type), EditorialModel);
    preloader.resolve(getPageLinksPath(), LinkWidgetsModel);
  }

  return preloader.load().then(modelState => {
    const vehicleEditorialRankings = modelState.get(rankingsPath, TypeRankingsModel);
    const siteServedAds = modelState.get(
      getCreativeConfigAdPathByVehicle(mobile, vehicle),
      NativeAdsCreativeConfigModel
    );

    const firstVehicle = vehicleEditorialRankings?.[0]?.segmentRatings?.[0]?.modelYear;
    if (firstVehicle) {
      preloader.set('forcedVehicleParams', SuggestedVehicleModel, {
        make: firstVehicle.makeSlug,
        model: firstVehicle.modelSlug,
        year: firstVehicle.year,
      });
    }

    const leaseDeals = modelState.get(TypeLeaseDealsPaths.getTypeLeaseDealsTypeCategoryPath(type), TypeLeaseDealsModel);

    const heroBgPaths = HERO_BG_PATHS_LAUREL;
    const imageHref = mobile ? heroBgPaths.sm : heroBgPaths.lg;
    const assetsToPreload = [{ href: imageHref, as: 'image' }];
    preloader.set('server.preloadedAssets', PageModel, assetsToPreload);
    const ads12484Recipe = ExperimentUtil.getForcedOrAssignedRecipeName({
      state: store.getState(),
      campaignName: 'ads-12484',
      defaultVal: 'ctrl',
    });

    store.dispatch(
      setCriticalCss({
        type,
        criticalCssHeight: 1750,
        hasAnchorNav: hasSubsegments(vehicleEditorialRankings) || TYPES_WITH_MOST_SOLD_GRAPH.includes(type),
        ...siteServedAdsCriticalCss(siteServedAds),
        topRatedPromo: !!modelState.get('top-rated-promo', FeatureFlagModel),
        ...buildLeaseDealsHiddenGemsCriticalCss({ vehicleEditorialRankings, leaseDeals }),
        ads12484Recipe,
      })
    );
    return preloader.load();
  });
}

export const pageDefinitionConfig = ({ type, featureFlags }) => ({
  name: 'new_type_index',
  category: 'new_type_index',
  preload: store => preload(store, type, true),
  preloadAboveFold: featureFlags?.typePreloadAboveFold ? store => preload(store, type) : null,
  component: props => <TypePageV2 type={type} {...props} />,
  decorator: TypePageDecorator,
  seo: (state, props) => defineSeoContent(state, props, type),
  options: {
    hasVehicleContext: true,
  },
});

export default params => pageDefinition(pageDefinitionConfig(params));
